import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS, LabelXS } from '@sorare/core/src/atoms/typography';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';

import { shortBaseballStats } from '@sorare/core/src/lib/baseball.ts';
import type { Color } from '@sorare/core/src/style/types.ts';
import type { StatThresholdsBar_taskStatThreshold } from './__generated__/index.graphql.ts';

const SegmentBar = styled(Vertical).attrs({ gap: 0.5 })`
  flex: 1;
  padding: var(--unit);
  background-color: var(--segment-color);
  min-height: 40px;
  justify-content: flex-end;
  align-items: flex-start;

  &:first-child {
    border-radius: var(--unit) 0 0 var(--unit);
  }
  &:last-child {
    border-radius: 0 var(--unit) var(--unit) 0;
  }
`;

const StatLabel = styled(LabelXS)`
  color: var(--c-black);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  font-weight: bold;
`;

const RewardsCell = styled(Horizontal).attrs({ gap: 0.5, center: true })`
  flex: 1;
`;

type Props = {
  statThresholds: StatThresholdsBar_taskStatThreshold[];
};

export const StatThresholdsBar = ({ statThresholds }: Props) => {
  if (!statThresholds.length) return null;

  const allSameStat = statThresholds.every(
    t => t.stat === statThresholds[0].stat
  );

  return (
    <Vertical gap={1}>
      <Horizontal gap={0}>
        <SegmentBar
          style={{ '--segment-color': 'var(--c-nd-150)' } as CSSProperties}
        >
          <StatLabel color="var(--c-nd-400)">0</StatLabel>
        </SegmentBar>
        {statThresholds.map(threshold => {
          const upperCaseStat = threshold.stat.toUpperCase();
          const statLabelNode =
            upperCaseStat in shortBaseballStats
              ? shortBaseballStats[
                  upperCaseStat as keyof typeof shortBaseballStats
                ]
              : threshold.stat;

          return (
            <SegmentBar
              key={`${threshold.stat}-${threshold.min}`}
              style={{ '--segment-color': threshold.color } as CSSProperties}
            >
              <StatLabel>
                {threshold.min}+{!allSameStat && <>&nbsp;{statLabelNode}</>}
              </StatLabel>
            </SegmentBar>
          );
        })}
      </Horizontal>
      <Horizontal gap={0}>
        <RewardsCell />
        {statThresholds.map(threshold => (
          <RewardsCell key={`${threshold.stat}-${threshold.min}-rewards`}>
            {threshold.rewardConfigs.map(rewardConfig => (
              <AnyRewardConfig
                key={rewardConfig.id}
                anyRewardConfig={rewardConfig}
                variant={Variant.TEXT}
                customText={({ rewardText }) => (
                  <BrandXXS color={threshold.color as Color}>
                    {rewardText}
                  </BrandXXS>
                )}
              />
            ))}
          </RewardsCell>
        ))}
      </Horizontal>
    </Vertical>
  );
};

StatThresholdsBar.fragments = {
  taskStatThreshold: gql`
    fragment StatThresholdsBar_taskStatThreshold on TaskStatThreshold {
      color
      min
      stat
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<StatThresholdsBar_taskStatThreshold>,
};
